import { useLanguage } from "../contexts/LanguageContext";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Calendar, ArrowRight, CheckCircle } from "lucide-react";

// 自定义导航链接组件，支持滚动到顶部
const NavLink = ({ to, children, className, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    e.preventDefault();

    // 如果已经在当前页面，直接滚动到顶部
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // 如果不在当前页面，先导航到页面，然后滚动到顶部
      navigate(to);
      // 使用setTimeout确保页面加载完成后再滚动
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }

    if (onClick) {
      onClick();
    }
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

export default function Kylendar() {
  const { tx } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handlePlayVideo = () => {
    setShowVideo(true);
  };

  // 预加载WebP格式图片
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      // 如果WebP失败，回退到JPG
      const jpgImg = new Image();
      jpgImg.onload = () => setImageLoaded(true);
      jpgImg.src = '/poster4.jpg';
    };
    img.src = '/poster4.webp';
  }, []);

  const handleLearnMore = () => {
    const element = document.getElementById('calendar-respects-time');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="kylendar-hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 to-green-50">
        {/* 优化的背景图片加载 */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500"
          style={{
            backgroundImage: imageLoaded ? 'url(/poster4.webp)' : 'url(/poster4-preview.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: imageLoaded ? 1 : 0.7,
            filter: imageLoaded ? 'none' : 'blur(1px)'
          }}
        />
        {/* Background Overlay for better text readability */}
        <div className="absolute inset-0 bg-white/70"></div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-emerald-500 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-500 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium mb-8 animate-bounce-in">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse-glow"></span>{tx("7.5-Inch Smart E-Paper Calendar")}</div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight animate-slide-in-up">
                <span className="text-gradient animate-gradient-shift">{tx("Kylendar")}</span><br />{tx("Smart E-Paper")}<br />{tx("Desktop Calendar")}</h1>

              <div className="inline-flex items-center space-x-2 text-slate-500 font-medium mb-8 animate-fade-in transition-colors hover:text-emerald-600">
                <span className="text-emerald-500 text-base">✨</span>
                <span>{tx("Now available with a smarter V2 experience")}</span>
              </div>

              <p className="text-xl text-gray-600 mb-10 max-w-2xl leading-relaxed animate-fade-in">{tx("A distraction-free E-Ink display for your home and office. See the time, weather, calendar, and headlines at a glance—without glowing screens or notifications.")}</p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12 items-center">
                <a
                  href="https://kylendar.intelevia.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button size="lg" className="w-full bg-slate-900 text-white hover:bg-emerald-600 px-8 text-base rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 group">{tx("Try V2 Beta")}<ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </a>

                <Button size="lg" variant="ghost" className="w-full sm:w-auto text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 text-base font-medium transition-all duration-300" onClick={handleLearnMore}>{tx("Learn More")}</Button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-500">
                <div className="flex items-center justify-center sm:justify-start animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 animate-pulse-glow flex-shrink-0" />
                  <span>{tx("4 Display Modes")}</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 animate-pulse-glow flex-shrink-0" />
                  <span className="text-center sm:text-left">{tx("43 countries")}</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 animate-pulse-glow flex-shrink-0" />
                  <span>{tx("9 Languages")}</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start animate-fade-in" style={{ animationDelay: '0.4s' }}>
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 animate-pulse-glow flex-shrink-0" />
                  <span>{tx("Eco-Friendly")}</span>
                </div>
              </div>
            </div>

            {/* Visual Element */}
            <div className="relative animate-slide-in-right group cursor-pointer">
              <a href="https://kylendar.intelevia.com/us/" target="_blank" rel="noopener noreferrer" className="absolute -top-4 -right-2 md:-right-4 z-20 bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-md text-slate-700 text-sm font-medium px-4 py-2 rounded-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-1.5 group/badge whitespace-nowrap">
                <span className="text-emerald-500">✨</span>{tx("Now available in V2")}<ArrowRight className="w-4 h-4 ml-0.5 opacity-50 group-hover/badge:translate-x-1 group-hover/badge:opacity-100 group-hover/badge:text-emerald-600 transition-all" />
              </a>
              <div className="aspect-[4/3] bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl flex items-center justify-center shadow-2xl animate-float overflow-hidden group-hover:shadow-[0_20px_40px_-15px_rgba(16,185,129,0.3)] transition-all duration-500">
                {!showVideo ? (
                  <div className="text-center">
                    <div className="w-32 h-32 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                      <Calendar className="w-16 h-16 text-emerald-600 animate-gradient-shift" />
                    </div>
                    <p className="text-gray-600 font-medium animate-fade-in">{tx("Kylendar Smart Calendar")}</p>
                    <p className="text-sm text-gray-500 mt-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>{tx("7.5-inch E-Paper Display")}</p>
                    <div className="mt-4">
                      <button
                        onClick={handlePlayVideo}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium cursor-pointer animate-pulse-glow"
                      >{tx("Watch Demo")}</button>
                    </div>
                    <div className="mt-4 flex justify-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                ) : (
                  <iframe
                    className="w-full h-full rounded-2xl"
                    src="https://www.youtube.com/embed/uZ04JYSP5aw?autoplay=1&mute=1&loop=1&playlist=uZ04JYSP5aw"
                    title={tx("Kylendar Demo Video")}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="calendar-respects-time" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">{tx("A Calendar That ")}<span className="text-gradient">{tx("Respects Your Time")}</span>
            </h2>
            <p className="text-lg text-emerald-600 font-medium mb-8">{tx("🌱 Because today shapes tomorrow.")}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* 封面图片 */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="/cover.jpg"
                  alt={tx("Kylendar Smart Calendar")}
                  className="w-full h-auto rounded-2xl shadow-2xl "
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>

            {/* 文字内容 */}
            <div className="order-1 lg:order-2">
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
                <p className="text-xl italic">{tx("We live our lives one quiet moment at a time.")}</p>
                <p className="text-xl italic">{tx("Every morning, we reach for our phones to check the time, the weather, the date— and fall into a scroll we never meant to start.")}</p>
                <p className="text-xl italic">{tx("Distraction becomes the first thing we do each day.")}</p>
                <p className="text-xl italic">{tx("But what if your morning began differently?")}</p>
                <p className="text-xl italic">{tx("What if the first screen you saw didn't pull you into noise— but gently brought you back to your own rhythm?")}</p>
                <p className="text-xl italic">{tx("Kylendar is a screen that never competes for your attention")}</p>
                <p className="text-xl italic">{tx("It simply shows what matters most:")}</p>
                <ul className="text-xl italic space-y-2">
                  <li>☀️ <strong>{tx("The weather ahead")}</strong></li>
                  <li>🗓️ <strong>{tx("Today's date")}</strong></li>
                  <li>📰 <strong>{tx("Essential headlines")}</strong></li>
                  <li>🌍 <strong>{tx("World clocks that connect you to others")}</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">{tx("See Kylendar in ")}<span className="text-gradient animate-gradient-shift">{tx("Action")}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in">{tx("Watch how Kylendar helps you reclaim focus—one glance at a time.")}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-2xl animate-scale-in">
              <CardContent className="p-0">
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    className="w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/4_OxeHecsR8?autoplay=0&mute=0&loop=1&playlist=4_OxeHecsR8&controls=1&modestbranding=1&rel=0"
                    title={tx("Kylendar Product Video")}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  >
                  </iframe>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Explore V2 Card */}
      <section className="py-12 bg-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="p-1 rounded-3xl bg-gradient-to-r from-emerald-100 via-teal-100 to-blue-100 shadow-xl shadow-emerald-900/5 hover:shadow-2xl hover:shadow-emerald-900/10 transition-shadow duration-500">
            <div className="bg-white/80 backdrop-blur-xl rounded-[22px] p-8 md:p-12 border border-white flex flex-col md:flex-row items-center justify-between gap-8 group">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-xs font-semibold tracking-wider uppercase mb-4 border border-slate-200">
                  <span className="text-emerald-500 mr-2 text-base leading-none">✨</span>{tx("New")}</div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 tracking-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-700 group-hover:to-teal-700 transition-colors duration-300">{tx("Meet the all-new Kylendar. Built for focus.")}</h2>
                <p className="text-slate-600 text-lg max-w-xl">{tx("A faster, cleaner way to work. Experience the enhanced V2 dashboard today.")}</p>
              </div>
              <div className="shrink-0 w-full md:w-auto">
                <a
                  href="https://kylendar.intelevia.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex md:inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white transition-all duration-300 rounded-2xl bg-slate-900 hover:bg-gradient-to-r hover:from-emerald-600 hover:to-teal-600 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/30 overflow-hidden relative"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-out"></div>
                  <span className="relative z-10 flex items-center">{tx("Explore V2")}<ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Kylendar Solves */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">{tx("🧩 What Kylendar ")}<span className="text-gradient">{tx("Solves for You")}</span>
            </h2>
            <p className="text-lg text-gray-600 italic max-w-2xl mx-auto">{tx("A Calm Information Hub for Busy Lives")}</p>
          </div>

          <div className="space-y-20">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🌐 Your Calendar. Your Language. Anywhere.")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* 语言支持图片 */}
                <div className="order-first lg:order-last">
                  <div className="relative">
                    <img
                      src="/languages.jpg"
                      alt={tx("Kylendar Multi-language Support")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* 语言支持文字内容 */}
                <div>
                  <p className="text-lg text-gray-700 mb-4">{tx("Choose from ")}<strong>{tx("9 supported languages")}</strong>{tx(" during setup — ")}<strong>{tx("English (EN), German (DE), French (FR), Spanish (ES), Italian (IT), Dutch (NL), Polish (PL), Portuguese (PT), and Indonesian (ID)")}</strong>{tx(" — so your Kylendar displays information in the language you know best.")}</p>
                  <p className="text-lg text-gray-700 mb-4">{tx("Perfect for:")}</p>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li><strong>{tx("Multilingual households")}</strong>{tx(" setting the right interface language for everyone")}</li>
                    <li><strong>{tx("Expats")}</strong>{tx(" making a new place feel like home")}</li>
                    <li><strong>{tx("Global teams")}</strong>{tx(" working seamlessly across cultures")}</li>
                  </ul>
                  <p className="text-lg text-gray-700">{tx("Changing the language is simple — just revisit the setup page when needed.")}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("📵 Noise-Free, Always-On Information")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Noise-Free GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Noise-Free.gif"
                      alt={tx("Kylendar Noise-Free Experience")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Noise-Free文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Tired of checking your phone for the date or weather and getting lost in notifications, ads, and apps?")}</li>
                    <li>{tx("Kylendar filters out digital noise—no pings, no popups, no distractions.")}</li>
                    <li>{tx("It quietly displays what matters most: your day, your time, your world")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("👁️ Gentle on the Eyes, Easy for Everyone")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Gentle on the Eyes文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("7.5-inch glare-free E-Ink screen with 800×480 resolution")}</li>
                    <li>{tx("No blue light, no backlight, no eye strain")}</li>
                    <li>{tx("Readable under bright sunlight and soothing at night")}</li>
                    <li>{tx("Ideal for children, seniors, and anyone who wants a restful digital experience")}</li>
                  </ul>
                </div>

                {/* Gentle on the Eyes图片 */}
                <div>
                  <div className="relative">
                    <img
                      src="/gentle_on_the_eyes.webp"
                      alt={tx("Kylendar Gentle on the Eyes")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🌦️ Weather at a Glance—Updated Automatically")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Weather GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/weather.gif"
                      alt={tx("Kylendar Weather Display")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Weather文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Real-time weather conditions, temperature, humidity, wind")}</li>
                    <li>{tx("3-day forecast always visible")}</li>
                    <li>{tx("Updated every 30–60 minutes via OpenWeatherMap")}</li>
                    <li>{tx("Never wonder whether to bring an umbrella or reschedule an outdoor plan")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("📅 Holiday Awareness Across 43 Countries/Region")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Holiday Awareness文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Automatically syncs national holidays in 43 countries/region")}</li>
                    <li>{tx("Great for families, global teams, remote workers, or international meetings")}</li>
                    <li>{tx("No app, no manual setup—just the holidays you need, always displayed")}</li>
                  </ul>
                </div>

                {/* Holiday Awareness图片 */}
                <div>
                  <div className="relative">
                    <img
                      src="/countries.jpg"
                      alt={tx("Kylendar Holiday Awareness")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("📰 Stay Informed Without the Scroll Trap")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Stay Informed GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Stay_Informed_Without_the_Scroll_Trap.gif"
                      alt={tx("Kylendar Stay Informed Without the Scroll Trap")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPkFwcC1GcmVlIFNldHVwPC90ZXh0Pgo8L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Stay Informed文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Top headlines updated every 1–2 hours from trusted global sources")}</li>
                    <li>{tx("Covers ")}<strong>{tx("tech, business, science, health, travel, entertainment, sports, politics, food, and general")}</strong></li>
                    <li>{tx("Choose your preferred categories during setup")}</li>
                    <li>{tx("Just the essentials—nothing to lure you into endless scrolling")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🌍 Global Time Zones at a Glance")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Global Time Zones文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Displays real-time clocks for up to 4 selected cities")}</li>
                    <li>{tx("Automatic daylight saving time adjustment")}</li>
                    <li>{tx("Flip-clock style display that's beautiful and functional")}</li>
                    <li>{tx("Perfect for international families and global teams")}</li>
                  </ul>
                </div>

                {/* Global Time Zones GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Global_Time_Zones_at_a_Glance.gif"
                      alt={tx("Kylendar Global Time Zones at a Glance")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🔁 Four Display Modes, One Button")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Four Display Modes图片 */}
                <div>
                  <div className="relative">
                    <img
                      src="/Four_Modes.gif"
                      alt={tx("Kylendar Four Display Modes")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Four Display Modes文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Brightday Mode — Calendar-focused daily view")}</li>
                    <li>{tx("Skycast Mode — Weather-focused forecast view")}</li>
                    <li>{tx("Headliner Mode — News headlines front and center")}</li>
                    <li>{tx("World Clock Mode — Real-time time zones for global life")}</li>
                  </ul>
                  <p className="text-lg text-gray-700 mt-4">{tx("Switch modes with a single button press. Adapt your screen to fit your space and your schedule.")}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🧑🔧 App-Free Setup, Easy for Everyone")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* App-Free Setup文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Built-in Wi-Fi hotspot for simple browser-based setup")}</li>
                    <li>{tx("Configure Wi-Fi, language, location, time zones, and news preferences via 192.168.4.1")}</li>
                    <li>{tx("No app, no account, no tech skills required—setup takes just minutes")}</li>
                    <li>{tx("Even grandparents can do it without assistance")}</li>
                  </ul>
                </div>

                {/* App-Free Setup图片 */}
                <div>
                  <div className="relative">
                    <img
                      src="/App-Free_Setup.png"
                      alt={tx("Kylendar App-Free Setup")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiPkFwcC1GcmVlIFNldHVwPC90ZXh0Pgo8L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🔋 Ultra-Low Power. Optional Battery for Portability")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Ultra-Low Power GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Ultra-Low_Power.gif"
                      alt={tx("Kylendar Ultra-Low Power")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Ultra-Low Power文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Power is only used during screen refresh")}</li>
                    <li>{tx("Content stays visible even when unplugged")}</li>
                    <li>{tx("Plugged-in use for 24/7 display, or go wireless with the optional battery pack")}</li>
                    <li>{tx("Battery life: Mini lasts 1–2 weeks; Standard lasts 4–6 weeks, depending on usage")}</li>
                    <li>{tx("Smart energy management ensures silent, stable operation")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🧱 Minimalist Design, Built to Blend In")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Minimalist Design文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Durable ABS plastic body with a clean matte finish")}</li>
                    <li>{tx("Lightweight: 300g | Slim profile: 10mm thick")}</li>
                    <li>{tx("Built-in stand for desktops and a wall-mount hole for vertical spaces")}</li>
                    <li>{tx("Fits effortlessly into kitchens, hallways, workspaces, and bedrooms")}</li>
                    <li>{tx("Looks like a modern decor piece, works like a smart assistant")}</li>
                  </ul>
                </div>

                {/* Minimalist Design GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Minimalist_Design.gif"
                      alt={tx("Kylendar Minimalist Design")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🔄 OTA Firmware Updates: Smarter Over Time")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* OTA Firmware Updates GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/OTA_Firmware_Updates.gif"
                      alt={tx("Kylendar OTA Firmware Updates")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* OTA Firmware Updates文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Automatic over-the-air updates via Wi-Fi")}</li>
                    <li>{tx("Adds new features, languages, layout improvements, and optimizations")}</li>
                    <li>{tx("No user action required—updates install silently overnight")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🌱 Eco-Friendly and Mindful by Design")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Eco-Friendly文字内容 */}
                <div>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("E-Ink uses no power when idle")}</li>
                    <li>{tx("No emissions, no light pollution, no screen fatigue")}</li>
                    <li>{tx("A sustainable alternative to power-hungry displays")}</li>
                    <li>{tx("Designed to run silently, constantly, and responsibly")}</li>
                  </ul>
                </div>

                {/* Eco-Friendly图片 */}
                <div>
                  <div className="relative">
                    <img
                      src="/Eco-Friendly_and_Mindful_by_Design.webp"
                      alt={tx("Kylendar Eco-Friendly and Mindful by Design")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🎨 Three Color Options to Match Your Space")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Three Color Options GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Three_Color_Options_to_Match_Your_Space.gif"
                      alt={tx("Kylendar Three Color Options")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Three Color Options文字内容 */}
                <div>
                  <p className="text-lg text-gray-700 mb-4">{tx("Kylendar is available in three minimalist color finishes:")}<br />{tx("Black, White, and Red—each designed to suit different moods, styles, and spaces.")}</p>
                  <ul className="text-lg text-gray-700 space-y-2">
                    <li>{tx("Black: Sleek and modern for office desks or tech-heavy setups")}</li>
                    <li>{tx("White: Clean and neutral, perfect for kitchens, bedrooms, and bright interiors")}</li>
                    <li>{tx("Red: Bold and warm, ideal for creative spaces or accent walls")}</li>
                  </ul>
                  <p className="text-lg text-gray-700 mt-4">{tx("Choose the color that blends into your home—or stands out just enough.")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Kylendar Changes Your Life */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">{tx("🌟 How Kylendar ")}<span className="text-gradient">{tx("Changes Your Life")}</span>
            </h2>
          </div>

          <div className="space-y-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("☀️ Mornings Without Screens")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Mornings Without Screens GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Mornings_Without_Screens.gif"
                      alt={tx("Kylendar Mornings Without Screens")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Mornings Without Screens文字内容 */}
                <div>
                  <p className="text-lg text-gray-700">{tx("Wake up to a calm, grounded morning. Instead of reaching for your phone and diving into social media or work emails, simply glance at Kylendar. It shows today's date, weather, and headlines—all in one quiet glance while you make your coffee. No glowing screens, no noise—just clarity.")}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("👨👩👧👦 Perfect for Parents")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Perfect for Parents文字内容 */}
                <div>
                  <p className="text-lg text-gray-700">{tx("The worry-free smart display for your family. Forget complicated gadgets. Kylendar is designed to be readable at a glance, even for elderly parents or non-tech-savvy users. It automatically updates holidays, weather, and calendars with no setup needed. Place it by the front door or dining table—everyone can stay informed before leaving the house.")}</p>
                </div>

                {/* Perfect for Parents GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Perfect_for_Parents.gif"
                      alt={tx("Kylendar Perfect for Parents")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🌍 Built for Remote Teams")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Built for Remote Teams GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Built_for_Remote_Teams.gif"
                      alt={tx("Kylendar Built for Remote Teams")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Built for Remote Teams文字内容 */}
                <div>
                  <p className="text-lg text-gray-700">{tx("Time zones, at a glance. If you work with teams or clients around the world, Kylendar's World Clock mode helps you schedule smarter. It shows up to four cities with real-time clocks and auto daylight saving time adjustments—perfect for knowing when to call Tokyo, Berlin, or San Francisco, without Googling every time.")}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("🎯 Healthy Routine for Kids")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Healthy Routine for Kids文字内容 */}
                <div>
                  <p className="text-lg text-gray-700">{tx("Help children build time awareness. Place Kylendar in your child's study space to help them understand days, dates, and routines. It teaches planning without the distractions of smartphones. Kids can see how many days until their next exam or holiday—all on a quiet, clutter-free E-Ink display that keeps focus where it belongs.")}</p>
                </div>

                {/* Healthy Routine for Kids GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Healthy_Routine_for_Kids.gif"
                      alt={tx("Kylendar Healthy Routine for Kids")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("👉 Versatile Spaces")}</h3>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-6">
                {/* Versatile Spaces GIF */}
                <div>
                  <div className="relative">
                    <img
                      src="/Versatile_Spaces.gif"
                      alt={tx("Kylendar Versatile Spaces")}
                      className="w-full h-auto rounded-2xl shadow-2xl "
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>

                {/* Versatile Spaces文字内容 */}
                <div>
                  <p className="text-lg text-gray-700">{tx("Blends into your life, beautifully. Whether it's mounted in your kitchen, standing in the hallway, or sitting quietly in your office, Kylendar delivers useful information without shouting for attention. No blinking lights. No sound. Just timeless, ambient presence—always there when you need it, never in the way.")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Modes Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">{tx("📱 Four Modes, Each with a ")}<span className="text-gradient">{tx("Focus")}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{tx("Switch between different display modes to match your current needs and environment.")}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <span className="text-emerald-600">{tx("Brightday")}</span>
              </h3>
              <p className="text-gray-700 mb-2">{tx("Simple, clean calendar layout (Date + Month + Year)")}</p>
              <p className="text-sm text-gray-500">{tx("Ideal for desks & living rooms")}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <span className="text-emerald-600">{tx("Skycast")}</span>
              </h3>
              <p className="text-gray-700 mb-2">{tx("Detailed weather information (Current Temp, Condition, Forecast)")}</p>
              <p className="text-sm text-gray-500">{tx("Use it when getting ready in the morning")}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <span className="text-emerald-600">{tx("Headliner")}</span>
              </h3>
              <p className="text-gray-700 mb-2">{tx("Real-time news update headlines on the main area")}</p>
              <p className="text-sm text-gray-500">{tx("Great for mealtimes & breaks")}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                <span className="text-emerald-600">{tx("World Clock")}</span>
              </h3>
              <p className="text-gray-700 mb-2">{tx("Four flip-clock style displays showing different time zones")}</p>
              <p className="text-sm text-gray-500">{tx("Perfect for international collaboration")}</p>
            </div>
          </div>

          {/* Four Modes GIF */}
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <img
                src="/Four_Modes.gif"
                alt={tx("Kylendar Four Modes")}
                className="w-full h-auto rounded-2xl shadow-2xl "
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Future-Ready Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">{tx("✅ Future-Ready: ")}<span className="text-gradient">{tx("Custom Interfaces Coming Soon")}</span>
            </h2>
            <p className="text-lg text-gray-600 italic mb-8">{tx("Kylendar is built with the future in mind.")}</p>
          </div>

          <div className="text-center">
            <p className="text-lg text-gray-700 mb-6">{tx("With a free account, you can use Kylendar for basic dashboards and device sync. Optional paid plans can unlock more workspaces, higher limits, and premium features, including the ability to fully customize your display interface— choose which data modules appear, rearrange layout zones, and integrate additional data sources based on your personal or professional needs.")}</p>
            <ul className="text-lg text-gray-700 space-y-2 mb-6">
              <li>{tx("Want a persistent to-do list?")}</li>
              <li>{tx("Need stock tickers or calendar feeds?")}</li>
              <li>{tx("Prefer a clean minimal layout with only time + weather?")}</li>
            </ul>
            <p className="text-lg text-gray-700 mb-6">{tx("You'll be able to design it your way.")}</p>
            <p className="text-lg text-gray-700 mb-8">
              💡 <strong>{tx("Premium customization capabilities will expand through future firmware upgrades and plan updates.")}</strong>
            </p>

            {/* Future-Ready Image */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <img
                  src="/Future-Ready.png"
                  alt={tx("Kylendar Future-Ready Features")}
                  className="w-full h-auto rounded-2xl shadow-2xl "
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 animate-slide-in-up">{tx("📋 Technical ")}<span className="text-gradient animate-gradient-shift">{tx("Specifications")}</span>
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in max-w-2xl mx-auto">{tx("Complete technical details for Kylendar smart e-paper calendar")}</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-3xl p-8 shadow-xl animate-scale-in">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Display")}</h3>
                    <p className="text-gray-700">{tx("Type & Size: 7\" High-Resolution E-Paper Display Ink screen")}</p>
                    <p className="text-gray-700">{tx("Resolution: 800×480 pixels")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Physical Dimensions")}</h3>
                    <p className="text-gray-700">{tx("Dimensions: 185 mm (W) × 120 mm (H) × 10 mm (D)")}</p>
                    <p className="text-gray-700">{tx("Weight: Approx. 300 g")}</p>
                    <p className="text-gray-700">{tx("Material: ABS Plastic Frame + e-Paper Front Panel")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Data Sources")}</h3>
                    <p className="text-gray-700">{tx("Holiday DB: Built-in for 44 countries")}</p>
                    <p className="text-gray-700">{tx("Weather Source: OpenWeatherMap")}</p>
                    <p className="text-gray-700">{tx("News Source: NewsAPI")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Connectivity & Power")}</h3>
                    <p className="text-gray-700">{tx("Connectivity: Wi-Fi 802.11 b/g/n (2.4 GHz)")}</p>
                    <p className="text-gray-700">{tx("Firmware: OTA updates")}</p>
                    <p className="text-gray-700">{tx("Charging Input: USB Type-C, DC 5V⎓1A")}</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="animate-fade-in" style={{ animationDelay: '0.5s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Languages")}</h3>
                    <p className="text-gray-700">{tx("9 languages including English (EN), German(DE), French (FR), Spanish (ES), Italian(IT), Dutch(NL), Polish(PL), Portuguese(PT), Indonesian(ID)")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.6s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Mounting & Display")}</h3>
                    <p className="text-gray-700">{tx("Mounting: Desk Stand + Wall-mount slot")}</p>
                    <p className="text-gray-700">{tx("Timezones: Up to 4 cities")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.7s' }}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tx("Operating Conditions")}</h3>
                    <p className="text-gray-700">{tx("Operating Temperature: 0°C to 40°C (32°F to 104°F)")}</p>
                    <p className="text-gray-700">{tx("Storage Temperature: -10°C to 50°C (14°F to 122°F)")}</p>
                    <p className="text-gray-700">{tx("Operating Humidity: 10%–85% RH, non-condensing")}</p>
                  </div>

                  <div className="animate-fade-in" style={{ animationDelay: '0.8s' }}>
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                      <p className="text-amber-800 font-medium">{tx("⚠️ Default version requires USB power and does not include a battery.")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Package Contents Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 animate-slide-in-up">{tx("📦 Package ")}<span className="text-gradient animate-gradient-shift">{tx("Contents")}</span>
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in">{tx("Everything you need to get started with Kylendar")}</p>
          </div>

          {/* Package Contents Image */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="relative">
              <img
                src="/Package_Contents.webp"
                alt={tx("Kylendar Package Contents")}
                className="w-full h-auto rounded-2xl shadow-2xl "
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Main Package Contents */}
              <div className="bg-white rounded-3xl p-8 shadow-xl animate-scale-in">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{tx("📦 What's Included")}</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-emerald-600 font-bold text-sm">1</span>
                    </div>
                    <p className="text-gray-700">
                      <strong>{tx("7‑Inch E-Paper Calendar")}</strong>{tx(" – Available in three elegant color options: Black, White, or Red (selectable after campaign)")}</p>
                  </div>

                  <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-emerald-600 font-bold text-sm">2</span>
                    </div>
                    <p className="text-gray-700">
                      <strong>{tx("Premium USB Type-C Charging Cable")}</strong>{tx(" (included by default)")}</p>
                  </div>

                  <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-emerald-600 font-bold text-sm">3</span>
                    </div>
                    <p className="text-gray-700">
                      <strong>{tx("User Manual")}</strong>
                    </p>
                  </div>

                  <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-emerald-600 font-bold text-sm">4</span>
                    </div>
                    <p className="text-gray-700">
                      <strong>{tx("Warranty Card")}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Battery Options */}
              <div className="bg-white rounded-3xl p-8 shadow-xl animate-scale-in" style={{ animationDelay: '0.2s' }}>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{tx("🔌 Battery Options (Add-On)")}</h3>

                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-2xl p-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                    <p className="text-gray-700 mb-4">
                      <strong>{tx("Default version requires USB power")}</strong>{tx(" (no battery included)")}</p>
                    <p className="text-gray-600 text-sm">{tx("Optional battery upgrades available:")}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-blue-600 font-bold text-sm">🔋</span>
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">
                          <strong>{tx("Battery Pack")}</strong>
                        </p>
                        <p className="text-gray-600 text-sm">{tx("lasts 5–7 days per charge")}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: '0.5s' }}>
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-green-600 font-bold text-sm">🔋</span>
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium">
                          <strong>{tx("Battery Max Pack")}</strong>
                        </p>
                        <p className="text-gray-600 text-sm">{tx("lasts up to 15–20 days per charge")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Crowdfunding Note */}
            <div className="mt-8 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <h4 className="text-lg font-semibold text-amber-800 mb-2">{tx("📦 In Crowdfunding Tiers Section：")}</h4>
              <p className="text-amber-700">{tx("Battery options are add-ons. The base unit does not include a built-in battery.")}</p>
              <p className="text-amber-700 mt-2">{tx("Choose your battery upgrade during the post-campaign survey.")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">{tx("Frequently Asked ")}<span className="text-gradient animate-gradient-shift">{tx("Questions")}</span>
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in max-w-2xl mx-auto">{tx("Find answers to common questions about Kylendar")}</p>
          </div>

          <div className="space-y-6">
            {/* FAQ Item 1 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("Does it support multiple languages?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Yes. Kylendar supports 9 languages: English (EN), German (DE), French (FR), Spanish (ES), Italian (IT), Dutch (NL), Polish (PL), Portuguese (PT), and Indonesian (ID).")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 2 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("How do I power on the device?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Simply connect the included USB Type-C cable to the device and plug it into a USB power adapter (5V, 1A recommended) or a powered USB port. The device will automatically turn on when powered.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 3 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("How do I set up the Wi-Fi connection?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Press the Wi-Fi Configuration Button once to activate the device's hotspot. Connect your smartphone, tablet, or computer to the hotspot, open a browser, and visit 192.168.4.1. Follow the on-screen instructions to select your Wi-Fi network, enter the password, and save the configuration.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 4 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("What should I do if I cannot connect to the device's hotspot?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    <li>{tx("Make sure the device is powered on and the Wi-Fi Configuration Button was pressed once.")}</li>
                    <li>{tx("Refresh the Wi-Fi list on your smartphone or computer and look for the device SSID.")}</li>
                    <li>{tx("Move closer to the device.")}</li>
                    <li>{tx("If problems persist, restart the device by unplugging and plugging it back in, then retry.")}</li>
                  </ol>
                </div>
              </details>
            </div>

            {/* FAQ Item 5 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("What should I do if the device cannot connect to my home Wi-Fi?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    <li>{tx("Double-check your Wi-Fi network name (SSID) and password entered in the configuration page.")}</li>
                    <li>{tx("Ensure your router broadcasts a 2.4 GHz Wi-Fi signal (5 GHz is not supported).")}</li>
                    <li>{tx("Place the device closer to the router during initial setup.")}</li>
                    <li>{tx("Restart your router and the device, then attempt configuration again.")}</li>
                  </ol>
                </div>
              </details>
            </div>

            {/* FAQ Item 6 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("How do I switch between different display modes?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed mb-4">{tx("Press the Mode Switch Button to cycle through the four available screen modes:")}</p>
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    <li><strong>{tx("Brightday Mode")}</strong>{tx(" — Calendar Focus")}</li>
                    <li><strong>{tx("Headliner Mode")}</strong>{tx(" — News Updates")}</li>
                    <li><strong>{tx("Skycast Mode")}</strong>{tx(" — Weather Focus")}</li>
                    <li><strong>{tx("World Clock Mode")}</strong>{tx(" — Multiple Time Zones")}</li>
                  </ol>
                  <p className="text-gray-600 leading-relaxed mt-4">{tx("Each press switches to the next mode in sequence.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 7 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("Can I manually refresh the weather or news information?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Currently, updates are performed automatically when the device is connected to Wi-Fi. Manual refresh is not necessary.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 8 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.8s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("Can I adjust the brightness or contrast of the screen?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("The E-Ink display has a fixed contrast that is optimized for readability. Brightness and contrast adjustments are not available as they are not needed with E-Ink technology.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 9 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '0.9s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("How long does it take to fully charge the device?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("The charging time depends on the battery level and the power source. With a standard 5V/1A adapter, it typically takes 2-4 hours to fully charge from empty.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 10 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '1.0s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("Can I mount the device on the wall?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Yes, Kylendar includes a wall-mount hole on the back. You can use standard wall-mounting hardware to securely attach it to your wall.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 11 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '1.1s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("How do I clean the device?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Use a soft, dry cloth to gently clean the screen and body. Avoid using any cleaning solutions or liquids. For stubborn marks, slightly dampen the cloth with water and wipe gently.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 12 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '1.2s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("Can I update the firmware?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("Yes, Kylendar supports OTA (Over-The-Air) firmware updates. When connected to Wi-Fi, the device will automatically check for and install updates when available.")}</p>
                </div>
              </details>
            </div>

            {/* FAQ Item 13 */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in" style={{ animationDelay: '1.3s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">{tx("What should I do if I lose my USB Type-C cable?")}</h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{tx("You can use any standard USB Type-C cable with a 5V/1A power adapter. The cable is not proprietary, so any compatible USB Type-C cable will work for charging the device.")}</p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-green">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{tx("Ready to Transform Your Daily Routine?")}</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">{tx("Join thousands of users who have discovered the power of calm, focused information display.")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <NavLink to="/contact">
              <Button size="lg" variant="outline" className="bg-white text-emerald-600 border-white">{tx("Contact Us")}<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </NavLink>
            <Button size="lg" variant="ghost" className="text-white" onClick={handleLearnMore}>{tx("Learn More")}</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
